import {
	Button,
	H1,
	HStack,
	IconCheck,
	IconCheckCircle,
	IconCompany,
	Text,
	Tooltip,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import StyledLink from 'components/common/Link';
import { Company } from 'domain/company/Company';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';
import { FunctionComponent } from 'react';
import { toDefaultDateFormat } from 'utils/dateUtils';
import { DeleteCompanyAction } from './DeleteCompanyAction/DeleteCompanyAction';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { SUBSCRIPTIONS_GET_COMPANY_BLACKLISTED_BY } from 'apiRoutes';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { ExternalLink } from '@montel/montelpro-shared-components';

const CompanyDetails: FunctionComponent<{
	company: Company;
	setIsEditing: (isEditing: boolean) => void;
	hasUsers: boolean;
	hasSubscriptions: boolean;
}> = ({ company, setIsEditing, hasUsers, hasSubscriptions }) => {
	const { isSuperAdmin, adminIsRestrictedToRole: isRestrictedToRole } =
		useAuthRoles();

	const { data: blacklistedByProviders } = useGetOnMount(
		`${SUBSCRIPTIONS_GET_COMPANY_BLACKLISTED_BY}?companyId=${company.id}`,
	);

	return (
		<>
			<VStack gap="xl">
				<HStack fullWidth align="center" justify="space-between">
					<HStack align="center" gap="md">
						<IconCompany boxSize="1.5rem" />
						<H1>{company.name}</H1>
						{company.isCustomer && (
							<Tooltip
								component={
									<IconCheckCircle
										boxSize="1.5rem"
										color="iconSuccess"
									/>
								}
							>
								This company is a customer
							</Tooltip>
						)}
					</HStack>
					<AccessControl
						allowedRoles={[
							AdminRole.SUPER_ADMIN,
							AdminRole.MONTEL_ACCOUNT_ADMIN,
						]}
					>
						<HStack align="center" gap="md">
							<Button
								text={'Edit company info'}
								isDisabled={isRestrictedToRole(AdminRole.SALES)}
								onClick={() => {
									setIsEditing(true);
								}}
							/>
							{isSuperAdmin && (
								<DeleteCompanyAction
									hasUsers={hasUsers}
									hasSubscriptions={hasSubscriptions}
									company={company}
								/>
							)}
						</HStack>
					</AccessControl>
				</HStack>
				<HStack fullWidth gap="xl">
					<LabelAndValue
						minWidth="30%"
						label="Younium ID"
						value={
							company.youniumId ? (
								<StyledLink
									to={`${process.env.REACT_APP_YOUNIUM_BASEURL}/accounts/${company.youniumId}`}
									target="_blank"
								>
									{company.youniumId}
								</StyledLink>
							) : (
								<Text color="textDisabled" fontStyle="italic">
									Not connected to Younium
								</Text>
							)
						}
					/>
					<LabelAndValue
						minWidth="30%"
						label="Younium Account Number"
						value={
							company.youniumAccountNumber ?? (
								<Text color="textDisabled" fontStyle="italic">
									No account number available
								</Text>
							)
						}
					/>
					<LabelAndValue
						minWidth="30%"
						label="Hubspot ID"
						value={
							company.hubspotId ? (
								<ExternalLink
									href={`https://app.hubspot.com/contacts/${process.env.REACT_APP_HUBSPOT_ACCOUNT_ID}/company/${company.hubspotId}`}
								>
									View in HubSpot
								</ExternalLink>
							) : (
								<Text color="textDisabled" fontStyle="italic">
									Not connected to Hubspot
								</Text>
							)
						}
					/>
				</HStack>
				<HStack fullWidth gap="xl">
					<LabelAndValue
						minWidth="30%"
						label="Country"
						value={company.country?.name ?? ''}
					/>
					<LabelAndValue
						minWidth="30%"
						label="Email domains"
						value={
							company.emailDomains?.length ? (
								company.emailDomains.map((domain) => (
									<Text key={domain}>{domain}</Text>
								))
							) : (
								<Text color="textDisabled" fontStyle="italic">
									None
								</Text>
							)
						}
					/>
					<LabelAndValue
						minWidth="30%"
						label="Created (UTC)"
						value={
							company.created
								? toDefaultDateFormat(company.created)
								: ''
						}
					/>
				</HStack>
				<HStack fullWidth gap="xl">
					<LabelAndValue
						minWidth="30%"
						label="Blacklisted by provider(s)"
						value={
							blacklistedByProviders
								?.map(
									(provider: {
										code: string;
										name: string;
									}) => provider.name,
								)
								.join(', ') || (
								<Text color="textDisabled" fontStyle="italic">
									None
								</Text>
							)
						}
					/>
				</HStack>
			</VStack>
		</>
	);
};

export default CompanyDetails;
